import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, User, Clock, Share2, Stethoscope } from 'lucide-react';
import '../styles/ConsultationDetails.css';

const ConsultationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedConsultations, setRelatedConsultations] = useState([]);

  const API_BASE_URL = 'https://darkslateblue-eel-151368.hostingersite.com/api';

  // دالة لتصحيح مسارات الصور المكررة
  const getCorrectImagePath = (imagePath) => {
    if (!imagePath) return null;
    
    const baseUrl = 'https://darkslateblue-eel-151368.hostingersite.com';
    
    if (imagePath.startsWith('http')) {
      // إصلاح المسارات المكررة
      const corrected = imagePath.replace(
        /uploads\/consultations\/uploads\/consultations\//g,
        'uploads/consultations/'
      );
      return corrected;
    }
    
    if (imagePath.startsWith('uploads/')) {
      return `${baseUrl}/storage/${imagePath}`;
    }
    
    if (imagePath.startsWith('storage/')) {
      return `${baseUrl}/${imagePath}`;
    }
    
    return `${baseUrl}/storage/${imagePath}`;
  };

  useEffect(() => {
    if (id) {
      fetchConsultation();
    }
  }, [id]);

  const fetchConsultation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // جلب الاستشارة المحددة
      const response = await fetch(`${API_BASE_URL}/consultations/${id}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'فشل في تحميل الاستشارة');
      }
      
      const result = await response.json();
      
      // التعامل مع أنواع البيانات المختلفة
      let consultationData;
      if (result.success && result.data) {
        consultationData = result.data;
      } else if (result.data) {
        consultationData = result.data;
      } else if (result) {
        consultationData = result;
      } else {
        throw new Error('لم يتم العثور على الاستشارة');
      }
      
      // تصحيح مسار الصورة
      consultationData.image = getCorrectImagePath(consultationData.image);
      
      setConsultation(consultationData);
      
      // جلب استشارات ذات صلة (من نفس الطبيب أو مشابهة)
      if (consultationData.doctor) {
        fetchRelatedConsultations(consultationData.doctor);
      }
    } catch (err) {
      setError(err.message || 'حدث خطأ في تحميل الاستشارة');
      console.error('Error fetching consultation:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedConsultations = async (doctor) => {
    try {
      // جلب جميع الاستشارات ثم تصفيتها
      const response = await fetch(`${API_BASE_URL}/consultations`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        let consultationsData;
        if (result.success && result.data) {
          consultationsData = result.data;
        } else if (result.data) {
          consultationsData = result.data;
        } else if (Array.isArray(result)) {
          consultationsData = result;
        } else {
          consultationsData = [];
        }
        
        // تصفية الاستشارات من نفس الطبيب وتصحيح مسارات الصور
        const filtered = consultationsData
          .filter(item => item.id !== parseInt(id) && item.doctor === doctor)
          .map(item => ({
            ...item,
            image: getCorrectImagePath(item.image)
          }))
          .slice(0, 3);
        
        // إذا لم يكن هناك ما يكفي من نفس الطبيب، أضف استشارات أخرى
        if (filtered.length < 3) {
          const otherConsultations = consultationsData
            .filter(item => item.id !== parseInt(id) && item.doctor !== doctor)
            .map(item => ({
              ...item,
              image: getCorrectImagePath(item.image)
            }))
            .slice(0, 3 - filtered.length);
          
          setRelatedConsultations([...filtered, ...otherConsultations]);
        } else {
          setRelatedConsultations(filtered);
        }
      }
    } catch (err) {
      console.error('Error fetching related consultations:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'تاريخ غير معروف';
    
    try {
      const date = new Date(dateString);
      
      // التحقق من أن التاريخ صالح
      if (isNaN(date.getTime())) {
        return 'تاريخ غير معروف';
      }
      
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'تاريخ غير معروف';
    }
  };

  const calculateTimeSince = (dateString) => {
    if (!dateString) return 'تاريخ غير معروف';
    
    try {
      const created = new Date(dateString);
      const now = new Date();
      
      // التحقق من أن التاريخ صالح
      if (isNaN(created.getTime())) {
        return 'تاريخ غير معروف';
      }
      
      const diffTime = Math.abs(now - created);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'نشرت اليوم';
      if (diffDays === 1) return 'نشرت منذ يوم';
      if (diffDays === 2) return 'نشرت منذ يومين';
      if (diffDays < 7) return `نشرت منذ ${diffDays} أيام`;
      if (diffDays < 30) return `نشرت منذ ${Math.floor(diffDays / 7)} أسابيع`;
      if (diffDays < 365) return `نشرت منذ ${Math.floor(diffDays / 30)} أشهر`;
      return `نشرت منذ ${Math.floor(diffDays / 365)} سنوات`;
    } catch {
      return 'تاريخ غير معروف';
    }
  };

  const handleShare = () => {
    if (!consultation) return;
    
    if (navigator.share) {
      navigator.share({
        title: consultation.title,
        text: consultation.description ? consultation.description.substring(0, 100) + '...' : '',
        url: window.location.href,
      })
      .then(() => console.log('تمت المشاركة بنجاح'))
      .catch((error) => console.log('خطأ في المشاركة', error));
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('تم نسخ الرابط إلى الحافظة'))
        .catch(err => console.error('فشل في نسخ الرابط:', err));
    }
  };

  if (loading) {
    return (
      <div className="consultation-details-loading" dir="rtl">
        <div className="loading-spinner"></div>
        <p className="loading-text">جاري تحميل الاستشارة...</p>
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="consultation-details-error" dir="rtl">
        <div className="error-container">
          <h2 className="error-title">حدث خطأ</h2>
          <p className="error-message">{error || 'الاستشارة غير موجودة'}</p>
          <button
            onClick={() => navigate('/consultations')}
            className="back-button"
          >
            العودة للاستشارات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="consultation-details-page" dir="rtl">
      <div className="consultation-details-container">
        {/* زر العودة */}
        <div className="back-button-container">
          <button
            onClick={() => navigate('/consultations')}
            className="back-button"
          >
            <ArrowRight className="back-icon" />
            <span>العودة للاستشارات</span>
          </button>
        </div>

        {/* الاستشارة الرئيسية */}
        <article className="consultation-main">
          {/* صورة الاستشارة */}
          <div className="consultation-image-section">
            <img
              src={consultation.image || 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=1200'}
              alt={consultation.title}
              className="consultation-image"
              onError={(e) => {
                console.error('فشل تحميل صورة الاستشارة:', consultation.image);
                e.target.src = 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=1200';
                
                // محاولة مسار بديل
                if (consultation.image) {
                  const altPath = consultation.image.replace(
                    'https://darkslateblue-eel-151368.hostingersite.com/storage/',
                    'https://darkslateblue-eel-151368.hostingersite.com/public/storage/'
                  );
                  setTimeout(() => {
                    const tempImg = new Image();
                    tempImg.onload = () => {
                      e.target.src = altPath;
                    };
                    tempImg.src = altPath;
                  }, 100);
                }
              }}
              onLoad={() => console.log('تم تحميل صورة الاستشارة بنجاح:', consultation.image)}
            />
            <div className="consultation-type-badge">
              <Stethoscope className="badge-icon" />
              <span className="badge-text">استشارة طبية</span>
            </div>
          </div>

          {/* محتوى الاستشارة */}
          <div className="consultation-content-section">
            <h1 className="consultation-title">{consultation.title}</h1>

            {/* معلومات الاستشارة */}
            <div className="consultation-meta-info">
              {consultation.doctor && (
                <div className="meta-item">
                  <User className="meta-icon" />
                  <span className="meta-text">{consultation.doctor}</span>
                </div>
              )}
              
              <div className="meta-item">
                <Calendar className="meta-icon" />
                <span className="meta-text">{formatDate(consultation.created_at || consultation.updated_at)}</span>
              </div>
              
              <div className="meta-item">
                <Clock className="meta-icon" />
                <span className="meta-text">{calculateTimeSince(consultation.created_at || consultation.updated_at)}</span>
              </div>
            </div>

            {/* وصف الاستشارة */}
            <div className="consultation-description">
              <p className="description-text">{consultation.description}</p>
            </div>

            {/* نصيحة طبية إضافية */}
            <div className="medical-advice">
              <h3 className="advice-title">
                <Stethoscope className="advice-icon" />
                نصيحة طبية
              </h3>
              <p className="advice-text">
                هذه الاستشارة للأغراض التعليمية فقط. يرجى استشارة طبيبك المعالج للحصول على تشخيص وعلاج مناسب لحالتك الصحية.
              </p>
            </div>

            {/* أزرار الإجراءات */}
            <div className="consultation-actions">
              <button onClick={handleShare} className="action-button share-button">
                <Share2 className="action-icon" />
                <span>مشاركة الاستشارة</span>
              </button>
              <button 
                onClick={() => navigate('/consultations')}
                className="action-button browse-button"
              >
                <ArrowRight className="action-icon" />
                <span>تصفح المزيد</span>
              </button>
            </div>
          </div>
        </article>

        {/* استشارات ذات صلة */}
        {relatedConsultations.length > 0 && (
          <div className="related-consultations-section">
            <h2 className="section-title">استشارات ذات صلة</h2>
            <div className="related-consultations-grid">
              {relatedConsultations.map(relatedConsultation => (
                <div
                  key={relatedConsultation.id}
                  className="related-consultation-card"
                  onClick={() => navigate(`/consultations/${relatedConsultation.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="related-consultation-image">
                    <img
                      src={relatedConsultation.image || 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=500'}
                      alt={relatedConsultation.title}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=500';
                      }}
                    />
                    <div className="related-consultation-badge">استشارة</div>
                  </div>
                  <div className="related-consultation-content">
                    <h3 className="related-consultation-title">{relatedConsultation.title}</h3>
                    <p className="related-consultation-description">
                      {relatedConsultation.description 
                        ? (relatedConsultation.description.length > 100 
                            ? relatedConsultation.description.substring(0, 100) + '...'
                            : relatedConsultation.description)
                        : 'لا يوجد وصف متاح'}
                    </p>
                    <div className="related-consultation-meta">
                      {relatedConsultation.doctor && (
                        <span className="related-consultation-doctor">
                          <User className="doctor-icon" size={14} />
                          {relatedConsultation.doctor}
                        </span>
                      )}
                      <span className="related-consultation-date">
                        {formatDate(relatedConsultation.created_at || relatedConsultation.updated_at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* اقتراحات إضافية */}
        <div className="additional-suggestions">
          <h3 className="suggestions-title">هل لديك استفسار طبي؟</h3>
          <p className="suggestions-text">
            تصفح مكتبتنا الكاملة من الاستشارات الطبية أو قم بزيارة مقالاتنا الصحية للحصول على معلومات موثوقة.
          </p>
          <div className="suggestions-buttons">
            <button
              onClick={() => navigate('/consultations')}
              className="primary-suggestion-button"
            >
              تصفح جميع الاستشارات
            </button>
            <button
              onClick={() => navigate('/articles')}
              className="secondary-suggestion-button"
            >
              تصفح المقالات الطبية
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationDetails;